import type { CanvasObject } from '../types';
import type { Artboard } from '../hooks/useCanvasTransform';
import { calculateOverlapArea } from './artboardUtils';
import { drawRoundedRect, createRoundedRectPath, adjustCornerRadiusForBorder } from './roundedRect';
import { getBorderCoordinates, alignForStroke } from './borderCoordinates';
import { getAbsRotationValues } from './rotationCache';
import { getBlendModeOperation } from './blendMode';
import type { GlobalCompositeOperation } from './blendMode';

/**
 * PNG 저장 기능
 * 아트보드를 PNG 이미지로 내보내기
 */

/**
 * 아트보드를 이미지로 내보내기
 * @param artboardId 아트보드 ID (null이면 모든 아트보드)
 * @param artboards 아트보드 배열
 * @param getObjectsArray 객체 배열을 반환하는 함수
 * @param format 이미지 포맷 ('png' | 'jpeg')
 * @param quality JPEG 품질 (0.0 ~ 1.0)
 * @param projectName 프로젝트 이름
 * @param scale 이미지 스케일 (1.0 = 100%, 0.3 = 30% 등)
 * @param isThumbnail 썸네일 저장 여부 (파일명에 thumb_ 접두사 추가)
 */
export const exportArtboardAsImage = async (
  artboardId: string | null,
  artboards: Artboard[],
  getObjectsArray: () => CanvasObject[],
  format: 'jpeg' | 'png' = 'png',
  quality: number = 1.0,
  projectName: string = '',
  scale: number = 1.0,
  isThumbnail: boolean = false
): Promise<void> => {
  const objects = getObjectsArray();
  
  // 단일 아트보드 저장
  if (artboardId) {
    const artboard = artboards.find(a => a.id === artboardId);
    if (!artboard) return;
    await exportSingleArtboard(artboard, artboards, objects, format, quality, projectName, null, scale, isThumbnail);
  } else {
    // 모든 아트보드 저장 - 다운로드 폴더에 저장
    // // File System Access API 지원 시 폴더 선택 (주석 처리)
    // let directoryHandle: any = null;
    // if ('showDirectoryPicker' in window) {
    //   try {
    //     directoryHandle = await (window as any).showDirectoryPicker();
    //   } catch (error: any) {
    //     // 사용자가 취소한 경우
    //     if (error.name === 'AbortError') {
    //       return; // 모든 저장 취소
    //     }
    //     console.warn('폴더 선택 실패, 개별 파일 저장으로 전환:', error);
    //     // 폴더 선택 실패 시 개별 파일 저장으로 fallback
    //   }
    // }
    
    // 모든 아트보드 저장 (200ms 간격으로 순차 저장) - 다운로드 폴더에 저장
    for (let index = 0; index < artboards.length; index++) {
      await new Promise(resolve => setTimeout(resolve, index * 200));
      await exportSingleArtboard(artboards[index], artboards, objects, format, quality, projectName, null, scale, isThumbnail);
    }
  }
};

/**
 * 단일 아트보드를 이미지로 내보내기
 */
async function exportSingleArtboard(
  artboard: Artboard,
  artboards: Artboard[],
  objects: CanvasObject[],
  format: 'jpeg' | 'png',
  quality: number,
  projectName: string,
  directoryHandle?: any,
  scale: number = 1.0,
  isThumbnail: boolean = false
): Promise<void> {
  // 아트보드에 속한 객체들 필터링
  let artboardObjects = objects.filter(obj => {
    // 아트보드가 고정된 객체는 artboardId로 직접 확인
    if (obj.artboardId) {
      return obj.artboardId === artboard.id;
    }
    
    // artboardId가 없는 경우에만 겹침 기준으로 계산
    let maxOverlapArea = 0;
    let bestArtboard: Artboard | null = null;
    
    for (const a of artboards) {
      const overlapArea = calculateOverlapArea(obj, a);
      if (overlapArea > maxOverlapArea) {
        maxOverlapArea = overlapArea;
        bestArtboard = a;
      }
    }
    
    if (bestArtboard === null) {
      return false;
    }
    
    return bestArtboard.id === artboard.id;
  });
  
  // 클리핑된 객체들도 추가
  const clippedObjects = objects.filter(obj => 
    obj.isClipped && 
    obj.clipMaskId && 
    obj.artboardId === artboard.id &&
    !artboardObjects.find(o => o.id === obj.id)
  );
  artboardObjects = [...artboardObjects, ...clippedObjects];

  // 원본 크기로 렌더링할 캔버스 생성 (스케일이 1.0이 아니면 원본 크기로 먼저 렌더링)
  const originalCanvas = document.createElement('canvas');
  originalCanvas.width = artboard.width;
  originalCanvas.height = artboard.height;
  const originalCtx = originalCanvas.getContext('2d');
  if (!originalCtx) return;
  // PNG 저장 시 고품질 안티앨리어싱 설정
  originalCtx.imageSmoothingEnabled = true;
  originalCtx.imageSmoothingQuality = 'high';
  
  // 렌더링은 원본 캔버스에 수행 (기존 코드와 호환을 위해 ctx 변수 사용)
  const ctx = originalCtx;

  // 배경 그리기
  ctx.fillStyle = artboard.color;
  ctx.fillRect(0, 0, artboard.width, artboard.height);
  
  // 아트보드 경계로 전체 렌더링 클립
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, artboard.width, artboard.height);
  ctx.clip();

  // zOrder 순서대로 정렬 (현재 프로젝트는 zOrder 배열로 관리)
  const sortedObjects = artboardObjects;

  // 클리핑 마스크 관계 파악
  const clippingGroups: { [key: string]: CanvasObject[] } = {};
  sortedObjects.forEach(obj => {
    if (obj.clipMaskId) {
      if (!clippingGroups[obj.clipMaskId]) {
        clippingGroups[obj.clipMaskId] = [];
      }
      clippingGroups[obj.clipMaskId].push(obj);
    }
  });

  // 객체들 그리기
  sortedObjects.forEach(obj => {
    const relativeX = obj.x - artboard.x;
    const relativeY = obj.y - artboard.y;
    
    // 클리핑된 객체는 건너뛰기 (별도 처리)
    if (obj.isClipped) {
      return;
    }
    
    // 아웃글로우: 객체보다 먼저 렌더
    if ((obj as any).outGlowEnabled) {
      const hasChildren = !!(clippingGroups[obj.id] && clippingGroups[obj.id].length > 0);
      if (hasChildren) {
        // 마스크 레이어 아웃글로우: 블렌드 모드와 투명도 적용
        ctx.save();
        const maskOpacity = typeof (obj as any).opacity === 'number' 
          ? Math.max(0, Math.min(100, (obj as any).opacity)) / 100 
          : 1;
        if (maskOpacity !== 1) ctx.globalAlpha *= maskOpacity;
        const maskBlendOp = getBlendModeOperation((obj as any).blendMode);
        if (maskBlendOp) {
          ctx.globalCompositeOperation = maskBlendOp;
        }
        renderOutGlowForExport(ctx, obj, relativeX, relativeY);
        ctx.restore();
      } else {
        renderOutGlowForExport(ctx, obj, relativeX, relativeY);
      }
    }

    // 클리핑 마스크 대상 객체 처리
    if (clippingGroups[obj.id] && clippingGroups[obj.id].length > 0) {
      const clipMaskObj = obj;
      const clippedObjects = clippingGroups[obj.id];

      // 부모 비주얼(부모 픽셀)과 마스크 알파 생성
      const parentBase = document.createElement('canvas');
      parentBase.width = artboard.width;
      parentBase.height = artboard.height;
      const pctx = parentBase.getContext('2d');
      if (pctx) {
        pctx.imageSmoothingEnabled = true;
        pctx.imageSmoothingQuality = 'high';
        pctx.save();
        if (clipMaskObj.rotation) {
          const mcx = relativeX + clipMaskObj.width / 2;
          const mcy = relativeY + clipMaskObj.height / 2;
          pctx.translate(mcx, mcy);
          pctx.rotate(clipMaskObj.rotation || 0);
          pctx.translate(-mcx, -mcy);
        }
        // 회전 없이 객체 본체만 렌더링 (회전은 이미 컨텍스트에 적용됨)
        const tempObj = { ...clipMaskObj, rotation: 0 };
        drawObjectBody(pctx, tempObj, relativeX, relativeY);
        
        // 클리핑 레이어와 겹치는 부분 제거 (교집합 제외)
        // 단, 블렌드모드나 투명도가 있는 클리핑 레이어는 제외하지 않음 (블렌드 대상이 필요)
        if (clippedObjects.length > 0) {
          // 마스크 레이어의 회전을 역회전시켜 원래 좌표계로 복원 (클리핑 레이어는 원래 좌표계에서 그려야 함)
          if (clipMaskObj.rotation) {
            const mcx = relativeX + clipMaskObj.width / 2;
            const mcy = relativeY + clipMaskObj.height / 2;
            pctx.translate(mcx, mcy);
            pctx.rotate(-(clipMaskObj.rotation || 0));
            pctx.translate(-mcx, -mcy);
          }
          
          pctx.globalCompositeOperation = 'destination-out';
          pctx.fillStyle = '#000000';
          
          for (const clippedObj of clippedObjects) {
            // 블렌드모드나 투명도가 있는지 확인
            const hasBlendMode = getBlendModeOperation((clippedObj as any).blendMode) !== null;
            const hasOpacity = typeof (clippedObj as any).opacity === 'number' && (clippedObj as any).opacity < 100;
            
            // 블렌드모드나 투명도가 있으면 교집합을 유지해야 하므로 제거하지 않음
            if (hasBlendMode || hasOpacity) {
              continue;
            }
            
            // 클리핑 레이어의 회전 고려
            const clippedRot = clippedObj.rotation || 0;
            const clippedHasRot = Math.abs(clippedRot) > 1e-6;
            
            if (clippedHasRot) {
              const clippedCx = (clippedObj.x - artboard.x) + clippedObj.width / 2;
              const clippedCy = (clippedObj.y - artboard.y) + clippedObj.height / 2;
              pctx.save();
              pctx.translate(clippedCx, clippedCy);
              pctx.rotate(clippedRot);
              pctx.translate(-clippedCx, -clippedCy);
            }
            
            // 클리핑 레이어의 정확한 경계 그리기
            const clippedRelativeX = clippedObj.x - artboard.x;
            const clippedRelativeY = clippedObj.y - artboard.y;
            
            if (clippedObj.type === 'rectangle') {
              const rTL = clippedObj.borderRadiusTopLeft || 0;
              const rTR = clippedObj.borderRadiusTopRight || 0;
              const rBL = clippedObj.borderRadiusBottomLeft || 0;
              const rBR = clippedObj.borderRadiusBottomRight || 0;
              const hasRadius = (rTL + rTR + rBL + rBR) > 0;
              
              if (hasRadius) {
                drawRoundedRect(pctx, clippedRelativeX, clippedRelativeY, clippedObj.width, clippedObj.height, rTL, rTR, rBL, rBR);
                pctx.fill();
              } else {
                pctx.fillRect(clippedRelativeX, clippedRelativeY, clippedObj.width, clippedObj.height);
              }
            } else if (clippedObj.type === 'circle') {
              pctx.beginPath();
              const cCx = clippedRelativeX + clippedObj.width / 2;
              const cCy = clippedRelativeY + clippedObj.height / 2;
              const cRx = clippedObj.width / 2;
              const cRy = clippedObj.height / 2;
              if (pctx.ellipse) {
                pctx.ellipse(cCx, cCy, cRx, cRy, 0, 0, Math.PI * 2);
              } else {
                pctx.arc(cCx, cCy, Math.min(cRx, cRy), 0, Math.PI * 2);
              }
              pctx.closePath();
              pctx.fill();
            } else {
              // image, text 등은 직사각형
              pctx.fillRect(clippedRelativeX, clippedRelativeY, clippedObj.width, clippedObj.height);
            }
            
            if (clippedHasRot) {
              pctx.restore();
            }
          }
          
          pctx.globalCompositeOperation = 'source-over';
          
          // 마스크 레이어의 회전을 다시 적용 (restore 전에 원래 상태로 복원)
          if (clipMaskObj.rotation) {
            const mcx = relativeX + clipMaskObj.width / 2;
            const mcy = relativeY + clipMaskObj.height / 2;
            pctx.translate(mcx, mcy);
            pctx.rotate(clipMaskObj.rotation || 0);
            pctx.translate(-mcx, -mcy);
          }
        }
        
        pctx.restore();
      }

      const maskAlpha = document.createElement('canvas');
      maskAlpha.width = artboard.width;
      maskAlpha.height = artboard.height;
      const ma = maskAlpha.getContext('2d');
      if (ma) {
        ma.imageSmoothingEnabled = true;
        ma.imageSmoothingQuality = 'high';
        ma.save();
        if (clipMaskObj.rotation) {
          const mcx = relativeX + clipMaskObj.width / 2;
          const mcy = relativeY + clipMaskObj.height / 2;
          ma.translate(mcx, mcy);
          ma.rotate(clipMaskObj.rotation || 0);
          ma.translate(-mcx, -mcy);
        }
        ma.fillStyle = '#ffffff';
        // 회전 없이 객체 본체만 렌더링 (회전은 이미 컨텍스트에 적용됨)
        const tempObj = { ...clipMaskObj, rotation: 0 };
        drawObjectBody(ma, tempObj, relativeX, relativeY, true);
        ma.restore();
      }

      // 백드롭: 현재까지 메인 컨텍스트에 그려진 픽셀을 마스크 영역으로 제한
      const backdrop = document.createElement('canvas');
      backdrop.width = artboard.width;
      backdrop.height = artboard.height;
      const bd = backdrop.getContext('2d');
      if (bd) {
        bd.imageSmoothingEnabled = true;
        bd.imageSmoothingQuality = 'high';
        bd.drawImage((originalCanvas as HTMLCanvasElement), 0, 0);
        bd.globalCompositeOperation = 'destination-in';
        bd.drawImage(maskAlpha, 0, 0);
        bd.globalCompositeOperation = 'source-over';
      }

      // 통합 블렌드 캔버스 생성: 모든 클리핑된 객체를 하나의 캔버스에 합성
      const blended = document.createElement('canvas');
      blended.width = artboard.width;
      blended.height = artboard.height;
      const bctx = blended.getContext('2d');
      if (!bctx) {
        return;
      }
      bctx.imageSmoothingEnabled = true;
      bctx.imageSmoothingQuality = 'high';
      
      // 백드롭 (배경 + 이전 레이어, 마스크 영역)
      if (bd) {
        bctx.drawImage(backdrop, 0, 0);
      }
      
      // 부모 비주얼 (마스크 객체 본체, 투명도는 마지막에 전체 적용)
      if (pctx) {
        bctx.drawImage(parentBase, 0, 0);
      }
      
      // 각 클리핑된 객체를 통합 블렌드 캔버스에 합성
      clippedObjects.forEach(clippedObj => {
        const cx = clippedObj.x - artboard.x;
        const cy = clippedObj.y - artboard.y;

        const childLayer = document.createElement('canvas');
        childLayer.width = artboard.width;
        childLayer.height = artboard.height;
        const clc = childLayer.getContext('2d');
        if (!clc) return;
        clc.imageSmoothingEnabled = true;
        clc.imageSmoothingQuality = 'high';
        if ((clippedObj as any).outGlowEnabled) {
          renderOutGlowForExport(clc, clippedObj, cx, cy);
        }
        drawObject(clc, clippedObj, cx, cy);
        clc.globalCompositeOperation = 'destination-in';
        clc.drawImage(maskAlpha, 0, 0);

        // 자식 레이어를 통합 블렌드 캔버스에 합성 (블렌드 모드 + 투명도 적용)
        const bm = (clippedObj as any).blendMode as (string | undefined);
        const mode = bm ? (bm === 'plus-lighter' ? 'lighter' : bm) : 'source-over';
        bctx.globalCompositeOperation = mode as GlobalCompositeOperation;
        const opacity = typeof (clippedObj as any).opacity === 'number' ? Math.max(0, Math.min(100, (clippedObj as any).opacity)) / 100 : 1;
        bctx.save();
        bctx.globalAlpha = opacity;
        bctx.drawImage(childLayer, 0, 0);
        bctx.restore();
        bctx.globalAlpha = 1;
        bctx.globalCompositeOperation = 'source-over';
      });
      
      // 마스크 객체 테두리를 통합 블렌드 캔버스에 포함 (투명도는 마지막에 전체 적용)
      if (clipMaskObj.borderColor && clipMaskObj.borderWidth && clipMaskObj.borderWidth > 0) {
        // 테두리를 별도 캔버스에 렌더링 (투명도 없이)
        const borderCanvas = document.createElement('canvas');
        borderCanvas.width = artboard.width;
        borderCanvas.height = artboard.height;
        const borderCtx = borderCanvas.getContext('2d');
        if (borderCtx) {
          borderCtx.imageSmoothingEnabled = true;
          borderCtx.imageSmoothingQuality = 'high';
          drawMaskObjectBorder(borderCtx, clipMaskObj, relativeX, relativeY, true);
          // 투명도 없이 그려진 테두리를 blended에 합성
          bctx.drawImage(borderCanvas, 0, 0);
        }
      }
      
      // 통합 블렌드 캔버스를 메인 캔버스에 합성 (마스크 객체의 블렌드 모드와 투명도 한 번만 적용)
      ctx.save();
      const maskOpacity = typeof (clipMaskObj as any).opacity === 'number' 
        ? Math.max(0, Math.min(100, (clipMaskObj as any).opacity)) / 100 
        : 1;
      const maskBlendOp = getBlendModeOperation((clipMaskObj as any).blendMode);
      if (maskBlendOp) {
        ctx.globalCompositeOperation = maskBlendOp;
      }
      if (maskOpacity !== 1) ctx.globalAlpha *= maskOpacity;
      ctx.drawImage(blended, 0, 0);
      ctx.restore();
    } else {
      // 일반 객체 그리기 (블렌드 모드 대응)
      const bm = (obj as any).blendMode as (string | undefined);
      if (bm) {
        const layer = document.createElement('canvas');
        layer.width = artboard.width;
        layer.height = artboard.height;
        const lctx = layer.getContext('2d');
        if (lctx) {
          lctx.imageSmoothingEnabled = true;
          lctx.imageSmoothingQuality = 'high';
          if ((obj as any).outGlowEnabled) {
            renderOutGlowForExport(lctx, obj, relativeX, relativeY);
          }
          drawObject(lctx, obj, relativeX, relativeY);
        }
        ctx.save();
        const mode = bm === 'plus-lighter' ? 'lighter' : bm;
        const opacity = typeof (obj as any).opacity === 'number' ? Math.max(0, Math.min(100, (obj as any).opacity)) / 100 : 1;
        ctx.globalCompositeOperation = mode as GlobalCompositeOperation;
        ctx.globalAlpha = opacity;
        ctx.drawImage(layer, 0, 0);
        ctx.restore();
      } else {
        const hasOpacity = typeof (obj as any).opacity === 'number';
        if (hasOpacity) {
          ctx.save();
          const alpha = Math.max(0, Math.min(100, (obj as any).opacity)) / 100;
          ctx.globalAlpha *= alpha;
        }
        drawObject(ctx, obj, relativeX, relativeY);
        if (hasOpacity) {
          ctx.restore();
        }
      }
    }
  });

  // 아트보드 클립 복원
  ctx.restore();
  
  // 스케일이 1.0이 아니면 원본 캔버스를 축소된 캔버스로 복사
  let finalCanvas: HTMLCanvasElement = originalCanvas;
  if (scale !== 1.0) {
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = Math.round(artboard.width * scale);
    scaledCanvas.height = Math.round(artboard.height * scale);
    const scaledCtx = scaledCanvas.getContext('2d');
    if (!scaledCtx) return;
    
    // 고품질 안티앨리어싱 설정
    scaledCtx.imageSmoothingEnabled = true;
    scaledCtx.imageSmoothingQuality = 'high';
    
    // 원본 캔버스를 축소된 크기로 그리기
    scaledCtx.drawImage(
      originalCanvas,
      0, 0, originalCanvas.width, originalCanvas.height,  // 원본 영역
      0, 0, scaledCanvas.width, scaledCanvas.height      // 대상 영역 (축소된 크기)
    );
    
    finalCanvas = scaledCanvas;
  }
  
  // 파일명 생성: 프로젝트이름_아트보드이름.확장자
  let baseFileName = '';
  if (projectName && projectName.trim()) {
    const safeProjectName = projectName.trim().replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
    const safeArtboardName = artboard.name.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
    baseFileName = `${safeProjectName}_${safeArtboardName}`;
  } else {
    baseFileName = artboard.name.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
  }
  
  // 썸네일인 경우 thumb_ 접두사 추가
  if (isThumbnail) {
    baseFileName = `thumb_${baseFileName}`;
  }
  
  const extension = format === 'png' ? 'png' : 'jpg';
  const fullFileName = `${baseFileName}.${extension}`;
  
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
  
  // Canvas를 Blob으로 변환
  const blob = await new Promise<Blob>((resolve, reject) => {
    finalCanvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to convert canvas to blob'));
      }
    }, mimeType, format === 'jpeg' ? quality : undefined);
  });
  
  // 파일 존재 여부 확인 함수 (File System Access API용) - 주석 처리
  // const checkFileExists = async (dirHandle: any, fileName: string): Promise<boolean> => {
  //   try {
  //     await dirHandle.getFileHandle(fileName, { create: false });
  //     return true; // 파일이 존재함
  //   } catch (error: any) {
  //     if (error.name === 'NotFoundError') {
  //       return false; // 파일이 없음
  //     }
  //     throw error; // 다른 에러는 재발생
  //   }
  // };
  
  // 폴더 핸들이 있으면 (다중 파일 저장) 해당 폴더에 저장 - 주석 처리
  // if (directoryHandle) {
  //   try {
  //     // 폴더 저장 시 중복 체크
  //     let finalFileName = fullFileName;
  //     let counter = 1;
  //     
  //     while (await checkFileExists(directoryHandle, finalFileName)) {
  //       finalFileName = `${baseFileName}_${counter}.${extension}`;
  //       counter++;
  //     }
  //     
  //     const fileHandle = await directoryHandle.getFileHandle(finalFileName, { create: true });
  //     const writable = await fileHandle.createWritable();
  //     await writable.write(blob);
  //     await writable.close();
  //     return; // 성공적으로 저장했으면 종료
  //   } catch (error: any) {
  //     console.error('폴더에 파일 저장 실패:', error);
  //     // 폴더 저장 실패 시 기존 방식으로 fallback
  //   }
  // }
  
  // File System Access API 사용 (단일 파일 저장 또는 폴더 저장 실패 시) - 주석 처리
  // if ('showSaveFilePicker' in window) {
  //   try {
  //     // 단일 파일 저장 시: 먼저 디렉토리를 선택하여 중복 체크
  //     let suggestedFileName = fullFileName;
  //     
  //     // 디렉토리 선택으로 중복 체크
  //     try {
  //       const tempDirHandle = await (window as any).showDirectoryPicker();
  //       let finalFileName = fullFileName;
  //       let counter = 1;
  //       
  //       // 디렉토리에서 중복 체크
  //       while (await checkFileExists(tempDirHandle, finalFileName)) {
  //         finalFileName = `${baseFileName}_${counter}.${extension}`;
  //         counter++;
  //       }
  //       
  //       suggestedFileName = finalFileName;
  //       
  //       // 디렉토리에서 파일 저장
  //       const fileHandle = await tempDirHandle.getFileHandle(suggestedFileName, { create: true });
  //       const writable = await fileHandle.createWritable();
  //       await writable.write(blob);
  //       await writable.close();
  //       
  //       return; // 성공적으로 저장했으면 종료
  //     } catch (dirError: any) {
  //       // 디렉토리 선택 취소 또는 실패 시 showSaveFilePicker로 fallback
  //       if (dirError.name === 'AbortError') {
  //         // 사용자가 디렉토리 선택을 취소한 경우 showSaveFilePicker 사용
  //         const fileHandle = await (window as any).showSaveFilePicker({
  //           suggestedName: suggestedFileName,
  //           types: [{
  //             description: format === 'png' ? 'PNG Image' : 'JPEG Image',
  //             accept: { [mimeType]: [`.${extension}`] }
  //           }]
  //         });
  //         
  //         const writable = await fileHandle.createWritable();
  //         await writable.write(blob);
  //         await writable.close();
  //         
  //         return; // 성공적으로 저장했으면 종료
  //       }
  //       throw dirError; // 다른 에러는 재발생
  //     }
  //   } catch (error: any) {
  //     // 사용자가 취소하거나 오류 발생 시 기존 방식으로 fallback
  //     if (error.name !== 'AbortError') {
  //       console.warn('File System Access API failed, falling back to download:', error);
  //     } else {
  //       // 사용자가 취소한 경우는 아무것도 하지 않음
  //       return;
  //     }
  //   }
  // }
  
  // 다운로드 폴더에 저장 (기존 방식)
  const finalFileName = fullFileName;
  
  const dataUrl = format === 'png'
    ? finalCanvas.toDataURL('image/png')
    : finalCanvas.toDataURL('image/jpeg', Math.max(0, Math.min(1, quality)) || 1.0);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = finalFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 객체 본체 그리기 (회전 포함)
 */
function drawObjectBody(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  relativeX: number,
  relativeY: number,
  maskOnly: boolean = false
): void {
  // 원본 좌표와 크기
  const origX = relativeX;
  const origY = relativeY;
  const origW = obj.width;
  const origH = obj.height;
  
  // 테두리 위치에 따른 객체 본체 크기 조정 (안티앨리어싱 문제 해결)
  // 텍스트는 제외하고, rectangle, circle, image만 적용
  let x = origX;
  let y = origY;
  let w = origW;
  let h = origH;
  let radiusScale = 1; // 둥근 모서리 반경 스케일
  
  const borderWidth = obj.borderWidth || 0;
  const borderPosition = obj.borderPosition || 'center';
  const hasBorder = borderWidth > 0 && obj.type !== 'text';
  
  if (hasBorder && borderPosition !== 'center') {
    const halfBorder = borderWidth / 2;
    
    if (borderPosition === 'outside') {
      // 외부 테두리: 객체 본체를 borderWidth/2만큼 확장
      x = origX - halfBorder;
      y = origY - halfBorder;
      w = origW + borderWidth;
      h = origH + borderWidth;
      // 둥근 모서리 반경도 비례 확장
      radiusScale = (origW + borderWidth) / origW; // 너비 기준 스케일
    } else if (borderPosition === 'inside') {
      // 내부 테두리: 객체 본체를 borderWidth/2만큼 축소
      x = origX + halfBorder;
      y = origY + halfBorder;
      w = Math.max(0, origW - borderWidth);
      h = Math.max(0, origH - borderWidth);
      // 둥근 모서리 반경도 비례 축소
      if (origW > 0) {
        radiusScale = (origW - borderWidth) / origW; // 너비 기준 스케일
      }
    }
  }
  
  // 텍스트는 별도의 색상(obj.color)으로 렌더링하므로 backgroundColor를 사용하지 않는다.
  // (backgroundColor를 사용하면 텍스트 본문 색상이 테두리 렌더링과 충돌할 수 있음)
  if (obj.type !== 'text') {
    ctx.fillStyle = obj.backgroundColor || '#007bff';
  }
  const hasRotation = !!obj.rotation;
  if (hasRotation) {
    ctx.save();
    const cx = x + w / 2;
    const cy = y + h / 2;
    ctx.translate(cx, cy);
    ctx.rotate(obj.rotation || 0);
    ctx.translate(-cx, -cy);
  }
  
  if (obj.type === 'rectangle') {
    let rTL = obj.borderRadiusTopLeft || 0;
    let rTR = obj.borderRadiusTopRight || 0;
    let rBL = obj.borderRadiusBottomLeft || 0;
    let rBR = obj.borderRadiusBottomRight || 0;
    
    // 테두리 위치에 따라 반경 스케일 조정
    if (hasBorder && borderPosition !== 'center') {
      rTL = rTL * radiusScale;
      rTR = rTR * radiusScale;
      rBL = rBL * radiusScale;
      rBR = rBR * radiusScale;
    }
    
    if (rTL || rTR || rBL || rBR) {
      drawRoundedRect(
        ctx,
        x,
        y,
        w,
        h,
        rTL,
        rTR,
        rBL,
        rBR
      );
      ctx.fill();
    } else {
      ctx.fillRect(x, y, w, h);
    }
  } else if (obj.type === 'circle') {
    ctx.beginPath();
    if (ctx.ellipse) {
      ctx.ellipse(x + w/2, y + h/2, w/2, h/2, 0, 0, 2 * Math.PI);
    } else {
      ctx.arc(x + w/2, y + h/2, Math.min(w, h)/2, 0, 2 * Math.PI);
    }
    ctx.fill();
  } else if (obj.type === 'image' && obj.image && typeof obj.image.naturalWidth !== 'undefined') {
    // 이미지 블러 효과 적용 (PNG 저장 시 항상 고품질)
    const blurEnabled = obj.blurEnabled && obj.blurRadius && obj.blurRadius > 0;
    if (blurEnabled) {
      // 블러 적용: 임시 캔버스에 그린 후 필터 적용
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = w;
      blurCanvas.height = h;
      const blurCtx = blurCanvas.getContext('2d');
      if (blurCtx) {
        blurCtx.imageSmoothingEnabled = true;
        blurCtx.imageSmoothingQuality = 'high';
        blurCtx.filter = `blur(${obj.blurRadius}px)`;
        blurCtx.drawImage(obj.image, 0, 0, w, h);
        blurCtx.filter = 'none';
        ctx.drawImage(blurCanvas, x, y);
      } else {
        // 컨텍스트 생성 실패 시 일반 렌더링
        try {
          ctx.drawImage(obj.image, x, y, w, h);
        } catch (e) {
          // 이미지 로드 실패 시 무시
        }
      }
    } else {
      // 블러 없이 일반 렌더링
      try {
        ctx.drawImage(obj.image, x, y, w, h);
      } catch (e) {
        // 이미지 로드 실패 시 무시
      }
    }
  } else if (obj.type === 'text' && obj.text) {
    // 텍스트는 원본 좌표와 크기 사용 (조정하지 않음)
    // PNG/JPG 내보내기 시에도 에디터와 동일하게 텍스트 색상을 사용
    ctx.fillStyle = obj.color || '#000000';
    const fontStyle = obj.fontStyle || 'normal';
    const fontWeight = obj.fontWeight || 'normal';
    const fontSize = obj.fontSize || 16;
    const fontFamily = obj.fontFamily || 'PretendardJP';
    // 폰트 패밀리 이름을 항상 따옴표로 감싸기
    const quotedFontFamily = `"${fontFamily}"`;
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
    ctx.textBaseline = 'alphabetic';
    
    const lines = obj.text.split('\n');
    const lineHeight = fontSize * 1.2;
    const measureLineWidth = (line: string) => ctx.measureText(line).width;
    const computeAlignedX = (line: string) => {
      const lineWidth = measureLineWidth(line);
      if (obj.textAlign === 'center') return origX + (origW - lineWidth) / 2;
      if (obj.textAlign === 'right') return origX + (origW - lineWidth);
      return origX; // left
    };
    
    lines.forEach((line, i) => {
      const textY = origY + fontSize + (i * lineHeight);
      const textX = computeAlignedX(line);
      ctx.fillText(line, textX, textY);
    });
  }
  
  if (hasRotation) {
    ctx.restore();
  }
}

/**
 * 객체 그리기 (본체 + 테두리)
 * 메인 캔버스 렌더링과 동일한 로직 사용
 */
function drawObject(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  relativeX: number,
  relativeY: number
): void {
  // 에디터 렌더링과 동일하게, 텍스트 + outside 테두리는
  // "테두리 → 본문" 순서로 그려서 텍스트 내부 색상을 보존한다.
  const isTextWithOutsideBorder =
    obj.type === 'text' &&
    !!obj.borderWidth &&
    obj.borderWidth > 0 &&
    obj.borderPosition === 'outside';

  if (isTextWithOutsideBorder) {
    // 1) 테두리 먼저
    if (obj.borderColor && obj.borderWidth && obj.borderWidth > 0) {
      const borderPos = obj.borderPosition || 'center';
      const rot = obj.rotation || 0;
      const hasRot = Math.abs(rot) > 1e-6;
      
      // 메인 캔버스 렌더링과 동일: inside 테두리는 원본 크기 기준, center/outside는 회전된 크기 기준
      const x = Math.round(relativeX);
      const y = Math.round(relativeY);
      const w = Math.round(obj.width);
      const h = Math.round(obj.height);
      const borderWidth = obj.borderWidth;
      const borderColor = obj.borderColor || '#000000';
      const cx = x + w / 2;
      const cy = y + h / 2;
      
      if (hasRot) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);
        ctx.translate(-cx, -cy);
      }
      
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = borderWidth;
      
      if (borderPos === 'outside') {
        // 텍스트 외부 테두리: 8방향 strokeText 반복 (기존 로직 그대로)
        if (obj.type === 'text' && obj.text) {
          const fontStyle = obj.fontStyle || 'normal';
          const fontWeight = obj.fontWeight || 'normal';
          const fontSize = obj.fontSize || 16;
          const fontFamily = obj.fontFamily || 'PretendardJP';
          ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
          ctx.textBaseline = 'alphabetic';
          
          const lines = obj.text.split('\n');
          const lineHeight = fontSize * 1.2;
          const measureLineWidth = (line: string) => ctx.measureText(line).width;
          const computeAlignedX = (line: string) => {
            const lineWidth = measureLineWidth(line);
            if (obj.textAlign === 'center') return x + (w - lineWidth) / 2;
            if (obj.textAlign === 'right') return x + (w - lineWidth);
            return x; // left
          };
          
          lines.forEach((line, i) => {
            const textX = computeAlignedX(line);
            const textY = y + fontSize + lineHeight * i;
            
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = borderWidth;
            for (let dx = -borderWidth; dx <= borderWidth; dx++) {
              for (let dy = -borderWidth; dy <= borderWidth; dy++) {
                if (dx !== 0 || dy !== 0) {
                  ctx.strokeText(line, textX + dx, textY + dy);
                }
              }
            }
          });
        }
      }
      
      if (hasRot) {
        ctx.restore();
      }
    }
    // 2) 본문(텍스트) 그리기
    drawObjectBody(ctx, obj, relativeX, relativeY);
  } else {
    // 일반 케이스: 본문 → 테두리
    drawObjectBody(ctx, obj, relativeX, relativeY);
    
    // 테두리 그리기
    if (obj.borderColor && obj.borderWidth && obj.borderWidth > 0) {
    const borderPos = obj.borderPosition || 'center';
    const rot = obj.rotation || 0;
    const hasRot = Math.abs(rot) > 1e-6;
    
    // 메인 캔버스 렌더링과 동일: inside 테두리는 원본 크기 기준, center/outside는 회전된 크기 기준
    const x = Math.round(relativeX);
    const y = Math.round(relativeY);
    const w = Math.round(obj.width);
    const h = Math.round(obj.height);
    const borderWidth = obj.borderWidth;
    const borderColor = obj.borderColor || '#000000';
    const cx = x + w / 2;
    const cy = y + h / 2;
    
    if (hasRot) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.translate(-cx, -cy);
    }
    
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    
    if (borderPos === 'outside') {
      // 외부 테두리: evenodd fill 방식
      // 메인 캔버스 렌더링과 동일: 회전은 이미 컨텍스트에 적용되어 있으므로 원본 크기 사용
      if (obj.type === 'rectangle') {
        const rTL = obj.borderRadiusTopLeft || 0;
        const rTR = obj.borderRadiusTopRight || 0;
        const rBL = obj.borderRadiusBottomLeft || 0;
        const rBR = obj.borderRadiusBottomRight || 0;
        const hasRadius = (rTL + rTR + rBL + rBR) > 0;
        
        if (hasRadius) {
          // 둥근 링: 외부/내부 둥근 사각형 경로 생성
          const outer = createRoundedRectPath(
            x - borderWidth,
            y - borderWidth,
            w + borderWidth * 2,
            h + borderWidth * 2,
            rTL + borderWidth,
            rTR + borderWidth,
            rBL + borderWidth,
            rBR + borderWidth
          );
          const inner = createRoundedRectPath(x, y, w, h, rTL, rTR, rBL, rBR);
          const ring = new Path2D(outer as any);
          (ring as any).addPath(inner);
          ctx.fillStyle = borderColor;
          (ctx as any).fill(ring, 'evenodd');
        } else {
          // 일반 사각 링
          const outerRect = new Path2D();
          outerRect.rect(
            x - borderWidth,
            y - borderWidth,
            w + borderWidth * 2,
            h + borderWidth * 2
          );
          const innerRect = new Path2D();
          innerRect.rect(x, y, w, h);
          const ring = new Path2D(outerRect as any);
          (ring as any).addPath(innerRect);
          ctx.fillStyle = borderColor;
          (ctx as any).fill(ring, 'evenodd');
        }
      } else if (obj.type === 'circle') {
        const rcx = Math.round(cx);
        const rcy = Math.round(cy);
        const outerRx = Math.max(0, Math.round(w / 2) + borderWidth);
        const outerRy = Math.max(0, Math.round(h / 2) + borderWidth);
        const innerRx = Math.max(0, Math.round(w / 2));
        const innerRy = Math.max(0, Math.round(h / 2));
        
        const outer = new Path2D();
        outer.ellipse(rcx, rcy, outerRx, outerRy, 0, 0, Math.PI * 2);
        const inner = new Path2D();
        inner.ellipse(rcx, rcy, innerRx, innerRy, 0, 0, Math.PI * 2);
        const ring = new Path2D(outer as any);
        (ring as any).addPath(inner);
        ctx.fillStyle = borderColor;
        (ctx as any).fill(ring, 'evenodd');
      } else if (obj.type === 'image') {
        // 이미지도 직사각형으로 처리
        const outerRect = new Path2D();
        outerRect.rect(
          x - borderWidth,
          y - borderWidth,
          w + borderWidth * 2,
          h + borderWidth * 2
        );
        const innerRect = new Path2D();
        innerRect.rect(x, y, w, h);
        const ring = new Path2D(outerRect as any);
        (ring as any).addPath(innerRect);
        ctx.fillStyle = borderColor;
        (ctx as any).fill(ring, 'evenodd');
      } else if (obj.type === 'text' && obj.text) {
        // 텍스트 외부 테두리: 8방향 strokeText 반복
        const fontStyle = obj.fontStyle || 'normal';
        const fontWeight = obj.fontWeight || 'normal';
        const fontSize = obj.fontSize || 16;
        const fontFamily = obj.fontFamily || 'PretendardJP';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textBaseline = 'alphabetic';
        
        const lines = obj.text.split('\n');
        const lineHeight = fontSize * 1.2;
        const measureLineWidth = (line: string) => ctx.measureText(line).width;
        const computeAlignedX = (line: string) => {
          const lineWidth = measureLineWidth(line);
          if (obj.textAlign === 'center') return x + (w - lineWidth) / 2;
          if (obj.textAlign === 'right') return x + (w - lineWidth);
          return x; // left
        };
        
        lines.forEach((line, i) => {
          const textX = computeAlignedX(line);
          const textY = y + fontSize + lineHeight * i;
          
          ctx.strokeStyle = borderColor;
          ctx.lineWidth = borderWidth;
          for (let dx = -borderWidth; dx <= borderWidth; dx++) {
            for (let dy = -borderWidth; dy <= borderWidth; dy++) {
              if (dx !== 0 || dy !== 0) {
                ctx.strokeText(line, textX + dx, textY + dy);
              }
            }
          }
        });
      }
    } else {
      // 중앙/내부 테두리: stroke 방식
      // 메인 캔버스 렌더링과 동일: 회전은 이미 컨텍스트에 적용되어 있으므로 원본 크기 사용
      // getBorderCoordinates는 원본 크기를 받아서 테두리 좌표를 계산
      const borderCoords = getBorderCoordinates(x, y, w, h, borderWidth, borderPos);
      const aligned = alignForStroke(borderCoords.x, borderCoords.y, borderCoords.width, borderCoords.height, borderWidth, borderPos);
      
      if (obj.type === 'rectangle') {
        const rTL = obj.borderRadiusTopLeft || 0;
        const rTR = obj.borderRadiusTopRight || 0;
        const rBL = obj.borderRadiusBottomLeft || 0;
        const rBR = obj.borderRadiusBottomRight || 0;
        const hasRadius = (rTL + rTR + rBL + rBR) > 0;
        
        if (hasRadius) {
          // 둥근 사각형 테두리: 보정된 반경 사용
          // 메인 캔버스 렌더링과 동일: 회전은 이미 컨텍스트에 적용되어 있으므로 원본 크기와 반경 사용
          ctx.beginPath();
          drawRoundedRect(
            ctx,
            aligned.x,
            aligned.y,
            aligned.width,
            aligned.height,
            adjustCornerRadiusForBorder(rTL, borderWidth, borderPos, w, h, aligned.width, aligned.height),
            adjustCornerRadiusForBorder(rTR, borderWidth, borderPos, w, h, aligned.width, aligned.height),
            adjustCornerRadiusForBorder(rBL, borderWidth, borderPos, w, h, aligned.width, aligned.height),
            adjustCornerRadiusForBorder(rBR, borderWidth, borderPos, w, h, aligned.width, aligned.height)
          );
          ctx.stroke();
        } else {
          // 일반 사각형 테두리
          ctx.strokeRect(aligned.x, aligned.y, aligned.width, aligned.height);
        }
      } else if (obj.type === 'image') {
        // 이미지는 직사각형으로 처리
        ctx.strokeRect(aligned.x, aligned.y, aligned.width, aligned.height);
      } else if (obj.type === 'circle') {
        const strokeCx = aligned.x + aligned.width / 2;
        const strokeCy = aligned.y + aligned.height / 2;
        const strokeRx = Math.max(0, aligned.width / 2);
        const strokeRy = Math.max(0, aligned.height / 2);
        ctx.beginPath();
        if (ctx.ellipse) {
          ctx.ellipse(strokeCx, strokeCy, strokeRx, strokeRy, 0, 0, Math.PI * 2);
        } else {
          ctx.arc(strokeCx, strokeCy, Math.min(strokeRx, strokeRy), 0, Math.PI * 2);
        }
        ctx.stroke();
      } else if (obj.type === 'text' && obj.text) {
        // 텍스트 중앙/내부 테두리: strokeText 사용
        const fontStyle = obj.fontStyle || 'normal';
        const fontWeight = obj.fontWeight || 'normal';
        const fontSize = obj.fontSize || 16;
        const fontFamily = obj.fontFamily || 'PretendardJP';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textBaseline = 'alphabetic';
        
        const lines = obj.text.split('\n');
        const lineHeight = fontSize * 1.2;
        const measureLineWidth = (line: string) => ctx.measureText(line).width;
        const computeAlignedX = (line: string) => {
          const lineWidth = measureLineWidth(line);
          if (obj.textAlign === 'center') return x + (w - lineWidth) / 2;
          if (obj.textAlign === 'right') return x + (w - lineWidth);
          return x; // left
        };
        
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        
        lines.forEach((line, i) => {
          const textX = computeAlignedX(line);
          const textY = y + fontSize + lineHeight * i;
          ctx.strokeText(line, textX, textY);
        });
      }
    }
    
      if (hasRot) {
        ctx.restore();
      }
    }
  }
}

/**
 * 마스크 레이어 테두리만 그리기
 * @param skipOpacity true이면 투명도 적용하지 않음 (blended 캔버스에 합성할 때 사용)
 */
function drawMaskObjectBorder(
  ctx: CanvasRenderingContext2D,
  maskObj: CanvasObject,
  relativeX: number,
  relativeY: number,
  skipOpacity: boolean = false
): void {
  if (!maskObj.borderColor || !maskObj.borderWidth || maskObj.borderWidth <= 0) {
    return;
  }
  
  ctx.save();
  // 마스크 레이어 테두리: 블렌드 모드와 투명도 적용 (skipOpacity가 false일 때만)
  const maskBlendOp = getBlendModeOperation((maskObj as any).blendMode);
  if (maskBlendOp && !skipOpacity) {
    ctx.globalCompositeOperation = maskBlendOp;
  } else {
    ctx.globalCompositeOperation = 'source-over';
  }
  if (!skipOpacity) {
    const borderOpacity = typeof (maskObj as any).opacity === 'number'
      ? Math.max(0, Math.min(100, (maskObj as any).opacity)) / 100
      : 1;
    ctx.globalAlpha = borderOpacity;
  }

  const borderPos = maskObj.borderPosition || 'center';
  const borderCoords = getBorderCoordinates(relativeX, relativeY, maskObj.width, maskObj.height, maskObj.borderWidth, borderPos);
  
  ctx.strokeStyle = maskObj.borderColor;
  ctx.lineWidth = maskObj.borderWidth;
  
  const rot = maskObj.rotation || 0;
  if (rot) {
    const mcx = relativeX + maskObj.width / 2;
    const mcy = relativeY + maskObj.height / 2;
    ctx.save();
    ctx.translate(mcx, mcy);
    ctx.rotate(rot);
    ctx.translate(-mcx, -mcy);
  }

  if (maskObj.type === 'rectangle') {
    const rTL = maskObj.borderRadiusTopLeft || 0;
    const rTR = maskObj.borderRadiusTopRight || 0;
    const rBL = maskObj.borderRadiusBottomLeft || 0;
    const rBR = maskObj.borderRadiusBottomRight || 0;
    const hasAnyRadius = (rTL + rTR + rBL + rBR) > 0;
    
    if (borderPos === 'outside') {
      if (!hasAnyRadius) {
        const outerRect = new Path2D();
        outerRect.rect(
          relativeX - maskObj.borderWidth,
          relativeY - maskObj.borderWidth,
          maskObj.width + maskObj.borderWidth * 2,
          maskObj.height + maskObj.borderWidth * 2
        );
        const innerRect = new Path2D();
        innerRect.rect(relativeX, relativeY, maskObj.width, maskObj.height);
        const ring = new Path2D(outerRect as any);
        (ring as any).addPath(innerRect);
        ctx.fillStyle = maskObj.borderColor as string;
        (ctx as any).fill(ring, 'evenodd');
      } else {
        const outer = createRoundedRectPath(
          relativeX - maskObj.borderWidth,
          relativeY - maskObj.borderWidth,
          maskObj.width + maskObj.borderWidth * 2,
          maskObj.height + maskObj.borderWidth * 2,
          rTL + maskObj.borderWidth,
          rTR + maskObj.borderWidth,
          rBL + maskObj.borderWidth,
          rBR + maskObj.borderWidth
        );
        const inner = createRoundedRectPath(
          relativeX,
          relativeY,
          maskObj.width,
          maskObj.height,
          rTL,
          rTR,
          rBL,
          rBR
        );
        const ring = new Path2D(outer as any);
        (ring as any).addPath(inner);
        ctx.fillStyle = maskObj.borderColor as string;
        (ctx as any).fill(ring, 'evenodd');
      }
    } else if (hasAnyRadius) {
      const aligned = alignForStroke(borderCoords.x, borderCoords.y, borderCoords.width, borderCoords.height, maskObj.borderWidth, borderPos);
      ctx.beginPath();
      drawRoundedRect(
        ctx,
        aligned.x,
        aligned.y,
        aligned.width,
        aligned.height,
        adjustCornerRadiusForBorder(rTL, maskObj.borderWidth, borderPos, maskObj.width, maskObj.height, aligned.width, aligned.height),
        adjustCornerRadiusForBorder(rTR, maskObj.borderWidth, borderPos, maskObj.width, maskObj.height, aligned.width, aligned.height),
        adjustCornerRadiusForBorder(rBL, maskObj.borderWidth, borderPos, maskObj.width, maskObj.height, aligned.width, aligned.height),
        adjustCornerRadiusForBorder(rBR, maskObj.borderWidth, borderPos, maskObj.width, maskObj.height, aligned.width, aligned.height)
      );
      ctx.stroke();
    } else {
      const aligned = alignForStroke(borderCoords.x, borderCoords.y, borderCoords.width, borderCoords.height, maskObj.borderWidth, borderPos);
      ctx.strokeRect(aligned.x, aligned.y, aligned.width, aligned.height);
    }
  } else if (maskObj.type === 'circle') {
    const aligned = alignForStroke(borderCoords.x, borderCoords.y, borderCoords.width, borderCoords.height, maskObj.borderWidth, borderPos);
    ctx.beginPath();
    if (ctx.ellipse) {
      ctx.ellipse(aligned.x + aligned.width/2, aligned.y + aligned.height/2, aligned.width/2, aligned.height/2, 0, 0, 2 * Math.PI);
    } else {
      ctx.arc(aligned.x + aligned.width/2, aligned.y + aligned.height/2, Math.min(aligned.width, aligned.height)/2, 0, 2 * Math.PI);
    }
    ctx.stroke();
  }

  if (rot) {
    ctx.restore();
  }
  ctx.restore();
}

/**
 * 포토샵식 아웃글로우 (export용): 마스크 → blur → 스프레드 알파 재매핑 → 색 적용 → 합성
 */
function renderOutGlowForExport(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  relativeX: number,
  relativeY: number
): void {
  const enabled = (obj as any).outGlowEnabled;
  const size = ((obj as any).outGlowSize == null ? 10 : (obj as any).outGlowSize);
  const color = (obj as any).outGlowColor || '#000000';
  const baseOpacity = (((obj as any).outGlowOpacity ?? 100) / 100) as number;
  const spreadPct = Math.max(0, Math.min(100, (obj as any).outGlowSpread ?? 0)) / 100;
  if (!enabled || size <= 0) return;

  const rot = obj.rotation || 0;
  const { cos: cosA, sin: sinA } = getAbsRotationValues(rot);
  
  // 텍스트 객체의 경우 실제 렌더링 크기(오프스크린 캔버스 확장)를 반영
  let objWidth = obj.width;
  let objHeight = obj.height;
  let objX = relativeX;
  let objY = relativeY;
  if (obj.type === 'text') {
    // 확장된 크기의 중심점이 원본 크기의 중심점과 일치하도록 위치 조정
    const expandedWidth = objWidth * 1.4;
    const expandedHeight = objHeight * 1.4;
    objX = relativeX - (expandedWidth - objWidth) / 2;
    objY = relativeY - (expandedHeight - objHeight) / 2;
    objWidth = expandedWidth;
    objHeight = expandedHeight;
  }
  
  const rotatedW = objWidth * cosA + objHeight * sinA;
  const rotatedH = objWidth * sinA + objHeight * cosA;
  const padding = Math.ceil(size * 3);
  const w = Math.max(1, Math.ceil(rotatedW + padding * 2));
  const h = Math.max(1, Math.ceil(rotatedH + padding * 2));

  // 1) 마스크
  const mask = document.createElement('canvas');
  mask.width = w; mask.height = h;
  const m = mask.getContext('2d'); if (!m) return;
  m.imageSmoothingEnabled = true;
  m.imageSmoothingQuality = 'high';
  
  // 텍스트 객체의 경우 확장된 캔버스 중앙에 원본 크기 텍스트를 배치
  const originalWidth = obj.type === 'text' ? obj.width : objWidth;
  const originalHeight = obj.type === 'text' ? obj.height : objHeight;
  const ox = padding + (rotatedW - originalWidth) / 2; const oy = padding + (rotatedH - originalHeight) / 2;
  m.save();
  if (obj.rotation) {
    const cx = ox + originalWidth / 2; const cy = oy + originalHeight / 2;
    m.translate(cx, cy); m.rotate(obj.rotation); m.translate(-cx, -cy);
  }
  m.fillStyle = '#ffffff';
  if (obj.type === 'rectangle') {
    if (obj.borderRadiusTopLeft || obj.borderRadiusTopRight || obj.borderRadiusBottomLeft || obj.borderRadiusBottomRight) {
      drawRoundedRect(m, ox, oy, originalWidth, originalHeight,
        obj.borderRadiusTopLeft || 0, obj.borderRadiusTopRight || 0,
        obj.borderRadiusBottomLeft || 0, obj.borderRadiusBottomRight || 0);
      m.fill();
    } else {
      m.fillRect(ox, oy, originalWidth, originalHeight);
    }
  } else if (obj.type === 'circle') {
    m.beginPath();
    if (m.ellipse) {
      m.ellipse(ox + originalWidth/2, oy + originalHeight/2, originalWidth/2, originalHeight/2, 0, 0, Math.PI*2);
    } else {
      m.arc(ox + originalWidth/2, oy + originalHeight/2, Math.min(originalWidth, originalHeight)/2, 0, Math.PI*2);
    }
    m.fill();
  } else if (obj.type === 'text' && obj.text) {
    const fontStyle = (obj as any).fontStyle || 'normal';
    const fontWeight = (obj as any).fontWeight || 'normal';
    const fontSize = (obj as any).fontSize || 16;
    const fontFamily = (obj as any).fontFamily || 'PretendardJP';
    // 폰트 패밀리 이름을 항상 따옴표로 감싸기
    const quotedFontFamily = `"${fontFamily}"`;
    m.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
    m.textBaseline = 'alphabetic';
    const lines = obj.text.split('\n');
    const lineHeight = fontSize * 1.2;
    const measure = (line: string) => m.measureText(line).width;
    const alignedX = (line: string) => {
      const tw = measure(line);
      if ((obj as any).textAlign === 'center') return ox + (originalWidth - tw) / 2;
      if ((obj as any).textAlign === 'right') return ox + (originalWidth - tw);
      return ox;
    };
    lines.forEach((line, i) => m.fillText(line, alignedX(line), oy + fontSize + lineHeight * i));
  } else {
    m.fillRect(ox, oy, originalWidth, originalHeight);
  }
  m.restore();

  // 2) 블러
  const blurred = document.createElement('canvas');
  blurred.width = w; blurred.height = h;
  const bctx = blurred.getContext('2d'); if (!bctx) return;
  bctx.imageSmoothingEnabled = true;
  bctx.imageSmoothingQuality = 'high';
  bctx.filter = `blur(${size}px)`;
  bctx.drawImage(mask, 0, 0);
  bctx.filter = 'none';

  // 3) 스프레드 알파 재매핑
  const img = bctx.getImageData(0, 0, w, h);
  const data = img.data; const threshold = 255 * (1 - spreadPct); const epsilon = 1;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i+3];
    let na = a < threshold ? (a / (threshold || 1)) * 255 : 255;
    na = Math.max(0, Math.min(255, na * baseOpacity));
    if (na < epsilon) na = 0;
    data[i] = 255; data[i+1] = 255; data[i+2] = 255; data[i+3] = na;
  }
  bctx.putImageData(img, 0, 0);

  // 4) 색 적용
  const colored = document.createElement('canvas');
  colored.width = w; colored.height = h;
  const cctx = colored.getContext('2d'); if (!cctx) return;
  cctx.imageSmoothingEnabled = true;
  cctx.imageSmoothingQuality = 'high';
  cctx.fillStyle = color; cctx.fillRect(0, 0, w, h);
  cctx.globalCompositeOperation = 'destination-in';
  cctx.drawImage(blurred, 0, 0);
  cctx.globalCompositeOperation = 'source-over';

  // 5) 합성 (아트보드 상대 좌표)
  ctx.drawImage(colored, relativeX - padding - (rotatedW - obj.width) / 2, relativeY - padding - (rotatedH - obj.height) / 2);
}

